import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbsenceType, AbsenceState } from 'src/app/time-tracking/models/absence-group';
import { IDayReportingObject } from 'src/app/time-tracking/models/day-reporting-object';
import { TimeEntryService } from 'src/app/time-tracking/services/time-entry.service';
import { TimeObject, TimeSpan } from '../helpers/time-span';
import { filePdfIcon, SVGIcon } from '@progress/kendo-svg-icons';
import { combineLatest, map } from 'rxjs';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { PageChangeEvent, SortSettings } from '@progress/kendo-angular-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { IGridState, PageSize } from 'src/app/core/models/base';
import { IDayReportingObjectSearchRequest } from 'src/app/time-tracking/models/day-reporting-object';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { PermissionStorageService } from 'src/app/authentication/storages/permission-storage.service';
import { TimeEntryAccessService } from 'src/app/time-tracking/services/time-entry-access.service';

@UntilDestroy()
@Component({
    selector: 'app-day-reporting-grid',
    templateUrl: './day-reporting-grid.component.html',
    styleUrls: ['./day-reporting-grid.component.scss'],
    standalone: false
})
export class DayReportingGridComponent implements OnInit {

  constructor(
    private timeEntryService: TimeEntryService,
    private tenantSettingsStore: TenantSettingsStoreService,
    private store: Store<AppStateInterface>,
    private permissionStorage: PermissionStorageService,
    private accessService: TimeEntryAccessService
  ) {
    this.store.select(currentEmployeeSelector)
      .pipe(untilDestroyed(this))
      .subscribe(employee => this.currentEmployeeId = employee?.id ?? null);
    this.accessService.manageableEmployeeIds$
      .pipe(untilDestroyed(this))
      .subscribe(ids => this.manageableIds = ids);
  }

  private currentEmployeeId: string | null = null;
  private manageableIds = new Set<string>();

  // Mirrors the server rule: one's own day takes SelfSignEntry, somebody else's SignEntry plus
  // the reach of the plain rights (hierarchy, or everyone with SearchAll).
  canSign(dayReportingObject: IDayReportingObject): boolean {
    const permissions = this.permissionStorage.getPermissions() ?? [];
    return dayReportingObject.employeeId === this.currentEmployeeId
      ? permissions.includes('Permissions.DayReportingObject.SelfSignEntry')
      : permissions.includes('Permissions.DayReportingObject.SignEntry') && this.manageableIds.has(dayReportingObject.employeeId);
  }

  private _dayReportingObjects: IDayReportingObject[] = [];
  @Input() public set dayReportingObjects(value: IDayReportingObject[]) {
    this._dayReportingObjects = value ?? [];
    if(this.searchRequest){
      // Keep the pager position in sync with the (server-side) request, so a filter that
      // resets to page 1 also resets the highlighted page.
      this.gridState.pageSize = this.searchRequest.pageSize;
      this.gridState.skip = ((this.searchRequest.pageNumber ?? 1) - 1) * this.searchRequest.pageSize;
    }
    this.applySort();
  }
  public get dayReportingObjects(): IDayReportingObject[] {
    return this._dayReportingObjects;
  }
  // Enables clicking the date column header to toggle the date sort direction.
  @Input() public sortable: boolean = false;
  public sortSettings: SortSettings = { allowUnsort: false, mode: 'single' };
  public sort: SortDescriptor[] = [];
  public sortedDayReportingObjects: IDayReportingObject[] = [];

  // Server-side paging (opt-in), mirroring the week grid. When enabled, the parent
  // provides the search request + total count and reloads on page/sort changes.
  @Input() public pageable: boolean = false;
  @Input() public total: number = 0;
  @Input() public searchRequest: IDayReportingObjectSearchRequest;
  public gridState: IGridState = { pageSize: 25, skip: 0 };

  get pageSizes() {
    return PageSize;
  }

  get gridData(): any {
    return this.pageable
      ? { data: this._dayReportingObjects, total: this.total }
      : this.sortedDayReportingObjects;
  }

  @Input() public isAdminView: boolean = false;
  // Admin sign action per day (opt-in). Kept off the nested week-detail day grids.
  @Input() public showSignAction: boolean = false;
  // Employee column (opt-in), for the admin day view that can span multiple employees.
  @Input() public showEmployee: boolean = false;
  @Input() public isEditable: boolean = true;
  @Input() public readOnly: boolean = false;
  @Input() public showFlexPreWorkChanges: boolean = false;
  @Input() public showtimeSupplementTimeChanges: boolean = false;
  @Output() onUpdateGrid: EventEmitter<undefined> = new EventEmitter();
  @Input() public set totalFlexTimeChange(totalFlexTimeChange: string) {
    if(totalFlexTimeChange){
      this.totalFlexTimeChangeObj = TimeSpan.parse(totalFlexTimeChange);
    }
  }
  @Input() public set preWorkTimeChange(preWorkTimeChange: string) {
    if(preWorkTimeChange){
      this.preWorkTimeChangeObj = TimeSpan.parse(preWorkTimeChange);
    }
  }
  @Input() public set totalWorkTime(totalWorkTime: string) {
    if(totalWorkTime){
      this.totalWorkTimeObj = TimeSpan.parse(totalWorkTime);
    }
  }
  // Grand totals over the whole selection (all pages), shown as a second footer line.
  @Input() public set grandWorkTime(grandWorkTime: string) {
    if(grandWorkTime){
      this.grandWorkTimeObj = TimeSpan.parse(grandWorkTime);
    }
  }
  @Input() public set grandFlexTime(grandFlexTime: string) {
    if(grandFlexTime){
      this.grandFlexTimeObj = TimeSpan.parse(grandFlexTime);
    }
  }
  @Input() public set grandPreWorkTime(grandPreWorkTime: string) {
    if(grandPreWorkTime){
      this.grandPreWorkTimeObj = TimeSpan.parse(grandPreWorkTime);
    }
  }
  @Input() public vacationReductionTotal: number;


  public filePdfIcon: SVGIcon = filePdfIcon;

  totalFlexTimeChangeObj: TimeSpan;
  preWorkTimeChangeObj: TimeSpan;
  timeSupplementTimeChangeObj: TimeSpan;
  totalWorkTimeObj: TimeSpan;
  grandWorkTimeObj: TimeSpan;
  grandFlexTimeObj: TimeSpan;
  grandPreWorkTimeObj: TimeSpan;

  get hasGrandTotals(): boolean {
    return !!(this.grandWorkTimeObj || this.grandFlexTimeObj || this.grandPreWorkTimeObj);
  }

  get absenceTypes(){
    return AbsenceType;
  }

  get absenceStates(){
    return AbsenceState;
  }

  vm$ = combineLatest([
    this.tenantSettingsStore.tenantSettings$
  ]).pipe(
    map(([
      tenantSettings
    ]) => ({
      tenantSettings
    }))
  );

  ngOnInit(): void {
  }

  onSortChange(sort: SortDescriptor[]) {
    this.sort = sort;
    if(this.pageable && this.searchRequest){
      // Server-side sorting: reload page 1 with the new order.
      const dir = sort[0]?.dir ?? 'desc';
      this.searchRequest.orderBy = [`Date ${dir}`];
      this.searchRequest.pageNumber = 1;
      this.gridState.skip = 0;
      this.onUpdateGrid.emit();
    } else {
      this.applySort();
    }
  }

  private applySort() {
    // In paged mode the server already returns the requested page in order, so keep it
    // as is. Otherwise sort client-side (with no descriptor orderBy keeps the incoming
    // order, so admin/nested views are unaffected).
    this.sortedDayReportingObjects = this.pageable
      ? this._dayReportingObjects
      : orderBy(this._dayReportingObjects, this.sort);
  }

  onPageChange({ skip, take }: PageChangeEvent) {
    const page = Math.round((skip + take) / take);

    this.gridState.skip = skip;
    this.gridState.pageSize = take;

    if(this.searchRequest){
      this.searchRequest.pageNumber = page;
      this.searchRequest.pageSize = take;
    }

    this.onUpdateGrid.emit();
  }

  onPageSizeChange(pageSize: number) {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload() {
    this.onUpdateGrid.emit();
  }

  onChangeEvent(){
    this.onUpdateGrid.emit();
  }

  onConfirmDay(id: string){
    this.timeEntryService.confirmDay({dayReportingObjectId: id})
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.onUpdateGrid.emit();
    })
  }

  canConfirmDay (date: Date): boolean{
    var result = new Date(date) < new Date();
    return result;
  }

  onSignDay(id: string){
    this.timeEntryService.signDay({dayReportingObjectId: id})
    .pipe(untilDestroyed(this))
    .subscribe(response => {
      this.onUpdateGrid.emit();
    })
  }

  canSignDay (date: Date): boolean{
    return new Date(date) < new Date();
  }

  getTimeObjClass(timeObject: TimeObject){
    if(timeObject?.factor < 0){
      return "error"
    }
  }

  // Both balances are reduced, so the stored values are negative - show them as the amount taken.
  overtimeHours(dayReportingObject: IDayReportingObject): number {
    return Math.abs(TimeSpan.parse(dayReportingObject?.overtimeChange ?? "00:00:00").getTotalSeconds()) / 3600;
  }

  flexTimeHours(dayReportingObject: IDayReportingObject): number {
    return Math.abs(TimeSpan.parse(dayReportingObject?.flexTimeChange ?? "00:00:00").getTotalSeconds()) / 3600;
  }

  hasAbsence(item: any): boolean {
    var res = item?.absences?.some(a => a.absenceGroup.absenceState === 10) ?? false;
    return res
  }
  
}
